<template>
  <v-card flat tile class="screener-builder-nav nav-container" style="border-bottom: 1px solid #ededed">
    <div class="screener-builder-nav__bar">
      <div class="screener-builder-nav__left">
        <WorkspaceMenu />

        <button type="button" class="screener-builder-nav__back" aria-label="Back" @click="onBack">
          <i class="ri-arrow-left-line"></i>
        </button>

        <div class="screener-builder-nav__title-row">
          <span class="screener-builder-nav__name">{{ screenerName }}</span>
          <button type="button" class="screener-builder-nav__edit" aria-label="Edit screener"
            @click="showEditModal = true">
            <i class="ri-pencil-line"></i>
            <span>Edit</span>
          </button>
          <span class="screener-builder-nav__badge" :class="`screener-builder-nav__badge--${qualifyMethod}`">
            {{ qualifyMethodLabel }}
          </span>
        </div>
      </div>

      <div class="screener-builder-nav__right">
        <HelpButton />
        <StartRecording />
      </div>
    </div>

    <CreateScreenerModal
      v-model="showEditModal"
      mode="edit"
      :form-type="formType"
      :initial-values="editInitialValues"
      :projects="projectOptions"
      @save="onSaveScreenerMeta"
    />
  </v-card>
</template>

<script>
import WorkspaceMenu from '@/components/menus/WorkspaceMenu.vue';
import HelpButton from '@/components/buttons/HelpButton.vue';
import StartRecording from '@/components/misc/StartRecording.vue';
import CreateScreenerModal from '@/views/optInForm/components/CreateScreenerModal.vue';
import {
  getScreenerDraft,
  updateScreenerDraft,
  getActiveScreenerDraftId,
  upsertScreenerDraft,
  createEmptyQuestion,
} from '@/views/optInForm/screenerDraftStore';
import { getPublishedScreener } from '@/views/optInForm/screenerViewMockData';
import {
  screenerLocation,
  screenerListPath,
  screenerRouteNames,
  getScreenerContext,
} from '@/views/optInForm/screenerNavigation';

export default {
  name: 'ScreenerBuilderNav',
  components: {
    WorkspaceMenu,
    HelpButton,
    StartRecording,
    CreateScreenerModal,
  },
  data() {
    return {
      nameVersion: 0,
      showEditModal: false,
    };
  },
  computed: {
    routeNames() {
      return screenerRouteNames(getScreenerContext(this.$route).isProject);
    },
    isPublishedView() {
      return [this.routeNames.questions, this.routeNames.responses].includes(
        this.$route.name,
      );
    },
    isPreviewRoute() {
      return this.$route.name === this.routeNames.preview;
    },
    screenerId() {
      return (
        this.$route.params.screenerId ||
        this.$route.query.id ||
        getActiveScreenerDraftId()
      );
    },
    draft() {
      // eslint-disable-next-line no-unused-expressions
      this.nameVersion;
      return getScreenerDraft(this.screenerId) || {};
    },
    published() {
      // eslint-disable-next-line no-unused-expressions
      this.nameVersion;
      if (!this.isPublishedView && !this.isPreviewRoute) return {};
      return getPublishedScreener(this.screenerId, this.$route.query) || {};
    },
    screenerName() {
      return (
        this.draft.name ||
        this.published.name ||
        this.$route.query.name ||
        'Untitled Screener'
      );
    },
    qualifyMethod() {
      return (
        this.draft.qualifyMethod ||
        this.published.qualifyMethod ||
        this.$route.query.method ||
        'automatic'
      );
    },
    qualifyMethodLabel() {
      return this.qualifyMethod === 'manual' ? 'Manual' : 'Automatic';
    },
    editInitialValues() {
      const { projectId } = getScreenerContext(this.$route);
      return {
        id: this.screenerId,
        name: this.screenerName,
        projectId: this.draft.projectId || projectId || null,
        projectName: this.draft.projectName || this.published.projectName || '',
        qualifyMethod: this.qualifyMethod,
      };
    },
    projectOptions() {
      const { projectId, isProject } = getScreenerContext(this.$route);
      if (!isProject) return [];
      return [
        {
          id: projectId,
          name: this.draft.projectName || 'Current Project',
        },
      ];
    },
    formType() {
      return getScreenerContext(this.$route).isProject ? 'screener' : 'opt_in';
    },
  },
  methods: {
    onBack() {
      if (this.isPreviewRoute) {
        this.$router.push(
          screenerLocation(this.$route, 'edit', {
            query: {
              id: this.screenerId,
              name: this.screenerName,
              method: this.qualifyMethod,
            },
          }),
        );
        return;
      }
      this.$router.push(screenerListPath(this.$route));
    },
    onSaveScreenerMeta(payload) {
      const id = payload.id || this.screenerId;
      const { projectId } = getScreenerContext(this.$route);
      const nextProjectId = payload.projectId || projectId || this.draft.projectId;
      const existing = getScreenerDraft(id);
      if (existing) {
        updateScreenerDraft(id, {
          name: payload.name,
          projectId: nextProjectId,
          projectName: payload.projectName,
          qualifyMethod: payload.qualifyMethod,
        });
      } else {
        upsertScreenerDraft(id, {
          name: payload.name,
          projectId: nextProjectId,
          projectName: payload.projectName,
          qualifyMethod: payload.qualifyMethod,
          questions: [createEmptyQuestion('single_choice')],
          createdAt: Date.now(),
        });
      }

      this.nameVersion += 1;

      const nextQuery = {
        ...this.$route.query,
        name: payload.name,
        method: payload.qualifyMethod,
      };

      if (this.isPublishedView || this.isPreviewRoute) {
        this.$router.replace({ query: nextQuery }).catch(() => {});
        return;
      }

      this.$router.push(
        screenerLocation(this.$route, 'edit', {
          query: {
            id,
            name: payload.name,
            method: payload.qualifyMethod,
          },
        }),
      );
    },
  },
};
</script>

<style scoped>
.screener-builder-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
}

.screener-builder-nav__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 64px;
  padding: 0 16px;
  box-sizing: border-box;
  background: #ffffff;
}

.screener-builder-nav__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.screener-builder-nav__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666666;
  cursor: pointer;
  flex-shrink: 0;
}

.screener-builder-nav__back:hover {
  background: #f5f5f5;
}

.screener-builder-nav__back i {
  font-size: 20px;
}

.screener-builder-nav__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.screener-builder-nav__name {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  color: #4c4c4c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.screener-builder-nav__edit {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 8px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #4C4C4C;
  cursor: pointer;
  flex-shrink: 0;
}

.screener-builder-nav__edit:hover {
  background: #4c4c4c1a;
}

.screener-builder-nav__edit i {
  font-size: 16px;
}

.screener-builder-nav__edit span {
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 600;
  line-height: 150%;
  color: #4c4c4c;
}

.screener-builder-nav__badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 1px 6px 2px;
  border-radius: 8px;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 150%;
  white-space: nowrap;
  flex-shrink: 0;
}

.screener-builder-nav__badge--automatic {
  background: #FFECF1;
  color: #B83280;
}

.screener-builder-nav__badge--manual {
  background: #EBF8FF;
  color: #2B6CB0;
}

.screener-builder-nav__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
</style>
